import { CheckIcon } from '@orn/shared-web/icons';
import * as React from 'react';

import { getWizardProgressState, WIZARD_PHASE_LABELS, WIZARD_PHASES } from '../../lib/onboarding/wizardPhases';

import type { WizardScreen } from '../../lib/onboarding/wizardPhases';

interface IOnboardingPhaseStepperProps {
  /**
   * The screen the user is currently on. Pass `null` once onboarding is
   * complete — every phase will render as "complete" with no current focus.
   */
  screen: WizardScreen | null;
}

/**
 * Horizontal four-phase progress indicator for the new onboarding wizard.
 * Each phase (About You / Your Loved One(s) / Details / Connect Accounts) is
 * one dot — current shows a focus ring, completed shows a check icon, future
 * is an empty stone-bordered dot. The connector line between consecutive
 * phase dots fills proportionally to the user's progress through the source
 * phase's sub-steps, so multi-step phases get "in-between" states while
 * single-step phases (Details, Connect Accounts) toggle 0 → 100% cleanly.
 *
 * Stateless: derive `screen` from `useOnboardingState` (or the equivalent
 * pre-onboarding flow state) and pass it through.
 *
 * Figma: Mantle - UX/UI, node 4173:68736 "Progress steps".
 */
const OnboardingPhaseStepper = ({ screen }: IOnboardingPhaseStepperProps) => {
  const { activePhaseIndex, lineFillRatios } = React.useMemo(() => getWizardProgressState(screen), [screen]);

  return (
    <div className="max-w-125 flex w-full flex-col items-center pt-12">
      <div className="gap-3.75 relative flex w-full items-start justify-center">
        <div
          aria-hidden
          className="pointer-events-none absolute top-[11px] flex h-[2px] items-center"
          style={{ left: 'calc(12.5% - 6px)', right: 'calc(12.5% - 6px)' }}
        >
          {lineFillRatios.map((fillRatio, lineIndex) => (
            <div key={lineIndex} className="bg-utility-stone-300 relative h-[2px] flex-1 overflow-hidden">
              <div className="bg-brand-solid absolute inset-y-0 left-0" style={{ width: `${fillRatio * 100}%` }} />
            </div>
          ))}
        </div>
        {WIZARD_PHASES.map((phase, phaseIndex) => {
          const isCurrent = phaseIndex === activePhaseIndex;
          const isComplete = phaseIndex < activePhaseIndex;
          return (
            <div key={phase} className="z-10 flex min-w-0 flex-1 flex-col items-center gap-3">
              <PhaseDot isComplete={isComplete} isCurrent={isCurrent} />
              <span
                className={`w-full text-center text-sm font-semibold ${
                  isCurrent ? 'text-brand-primary' : 'text-secondary'
                }`}
              >
                {WIZARD_PHASE_LABELS[phase]}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

interface IPhaseDotProps {
  isCurrent: boolean;
  isComplete: boolean;
}

const PhaseDot = ({ isCurrent, isComplete }: IPhaseDotProps) => {
  if (isCurrent) {
    return (
      <span className="bg-brand-solid relative flex size-6 shrink-0 items-center justify-center rounded-full shadow-[0_0_0_2px_var(--color-bg-primary),0_0_0_4px_var(--color-bg-brand-solid)]">
        <span className="size-2 shrink-0 rounded-full bg-white" />
      </span>
    );
  }
  if (isComplete) {
    return (
      <span className="bg-brand-solid flex size-6 shrink-0 items-center justify-center rounded-full">
        <CheckIcon aria-hidden className="size-3 shrink-0 text-white" strokeWidth={2.5} />
      </span>
    );
  }
  return (
    <span className="bg-app border-utility-stone-300 flex size-6 shrink-0 items-center justify-center rounded-full border-[1.5px]">
      <span className="bg-utility-stone-300 size-2 shrink-0 rounded-full" />
    </span>
  );
};

export default OnboardingPhaseStepper;
